import type { PlanReadOnlyKind } from "@/components/freemium/PlanReadOnlyDialog";
import type { PlanUsageDisplay } from "@/lib/billing/plan-usage";
import {
  FREE_TIER_MAX_STARRED_NOTES,
  maxScrollsPerFolderForPlan,
} from "@/lib/plan-limits";
import type { Folder, Scroll, Workspace } from "@/lib/supabase";

/** Matches server `countLiveScrollsInWorkspaceFolder` (non-deleted rows). */
export function countLiveScrollsInFolder(notes: readonly Scroll[]): number {
  return notes.filter((note) => !note.is_deleted).length;
}

/** Matches server starred cap input (non-deleted, starred scroll rows). */
export function countStarredScrolls(scrolls: readonly Scroll[]): number {
  return scrolls.filter((scroll) => !scroll.is_deleted && scroll.is_starred)
    .length;
}

/** Matches server publish cap input — live published rows only (not paused). */
export function countLivePublishedScrolls(scrolls: readonly Scroll[]): number {
  const ids = new Set<string>();
  for (const scroll of scrolls) {
    if (scroll.is_deleted || !scroll.is_published) continue;
    if (scroll.publish_paused) continue;
    ids.add(scroll.id);
  }
  return ids.size;
}

export function resolveWriteBlockKind(input: {
  workspace?: Pick<Workspace, "plan_locked"> | null;
  folder?: Pick<Folder, "plan_locked"> | null;
  scroll?: Pick<Scroll, "plan_locked" | "publish_paused"> | null;
  action?: "write" | "publish";
}): PlanReadOnlyKind | null {
  if (input.workspace?.plan_locked) return "workspace";
  if (input.folder?.plan_locked) return "folder";
  if (input.scroll?.plan_locked) return "note";
  if (input.action === "publish" && input.scroll?.publish_paused) {
    return "publish";
  }
  return null;
}

export function errorCodeFromUnknown(err: unknown): string | undefined {
  if (err instanceof Error) {
    return (err as Error & { code?: string }).code;
  }
  return undefined;
}

export function isFolderScrollLimitReached(
  liveCount: number,
  isPro: boolean,
): boolean {
  return liveCount >= maxScrollsPerFolderForPlan(isPro);
}

export function isFolderAtNoteLimit(
  notes: readonly Scroll[],
  isPro: boolean,
): boolean {
  return isFolderScrollLimitReached(countLiveScrollsInFolder(notes), isPro);
}

export function isPublishLimitReached(
  usage: Pick<PlanUsageDisplay, "isPro" | "publishedCount" | "maxPublished">,
  alreadyPublished: boolean,
): boolean {
  if (usage.isPro || alreadyPublished) return false;
  return usage.publishedCount >= usage.maxPublished;
}

export function isStarLimitReached(
  usage: Pick<PlanUsageDisplay, "isPro" | "starredCount">,
  scroll: Pick<Scroll, "is_starred">,
  fallbackStarredCount?: number,
): boolean {
  if (usage.isPro || scroll.is_starred) return false;
  const starredCount = Math.max(
    usage.starredCount,
    fallbackStarredCount ?? 0,
  );
  return starredCount >= FREE_TIER_MAX_STARRED_NOTES;
}

